import React from 'react';
import { Link } from 'react-router-dom';
import aOrAn from 'indefinite';

// Material UI
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

// Data
import { toListSentence } from '../../../lib/helpers/conversion.helpers';
import { pluralTerm } from '../../../lib/helpers/term.helper';
import { pluralize } from '../../../lib/helpers/text.helpers';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';
import { messages } from './unassigned-players-message.messages';

// Components
import { Bold } from '../../_core/_ui/typography.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';

interface IUnassignedPlayersMessage {
  ungroupedPlayers?: IGroupStageUngroupedPlayers | null;
  shortTrackRuns?: IGroupStageShortTrackRun[];
  recommendedPlayerLimits?: Array<{ index: number; recommendedLimit: number }>;
  playerLimits: IPlayerLimit[];
  positionHash: { [key: string]: string };
}

// Readiness blocks the grouping until every active player has a group and a session; the
// server works out both gaps and the position-limit fix, this only names them.
export const UnassignedPlayersMessage: React.FC<IUnassignedPlayersMessage> = ({
  ungroupedPlayers,
  shortTrackRuns,
  recommendedPlayerLimits,
  playerLimits,
  positionHash
}) => {
  const termLookup = useCompanySportTerminology();
  const athlete = termLookup[SportTerminology.ATHLETE].toLowerCase();
  const athletes = pluralTerm(athlete);
  const session = termLookup[SportTerminology.SESSION].toLowerCase();
  const sessions = pluralTerm(session);

  const runs = shortTrackRuns || [];
  // Not memoized: a min over the few short runs, not fed to any hook dep array.
  const narrowestRun = Math.min(...runs.map((run) => run.tracks));
  const stageList = (run: IGroupStageShortTrackRun) => toListSentence(run.stages.map((stage) => `"${stage.name}"`));
  const limitChanges = (recommendedPlayerLimits || []).flatMap(({ index, recommendedLimit }) => {
    const playerLimit = playerLimits[index];
    if (!playerLimit) return [];
    const positionNames = playerLimit.positions.map((positionId) => positionHash[positionId]).filter(Boolean);
    return [<IntlMsg {...messages.change} values={{ name: toListSentence(positionNames), from: playerLimit.limit, to: <Bold>{recommendedLimit}</Bold> }} />];
  });

  return (
    <Box>
      <Typography>
        <IntlMsg {...messages.heading} values={{ athlete, aSession: aOrAn(session) }} />
      </Typography>
      <ul>
        {(ungroupedPlayers?.positions || []).map(({ positionId, count }) => (
          <li key={positionId || 'none'}>
            <Typography>
              {positionId && positionHash[positionId]
                ? <IntlMsg {...messages.ungrouped} values={{ count, athletesPlay: pluralize(count, `${athlete} plays`, `${athletes} play`), position: <Bold>{positionHash[positionId]}</Bold> }} />
                : <IntlMsg {...(positionId ? messages.unknownPosition : messages.noPosition)} values={{ count, athletesHave: pluralize(count, `${athlete} has`, `${athletes} have`) }} />}
            </Typography>
          </li>
        ))}
        {runs.map((run) => (
          <li key={run.stages[0]._id}>
            <Typography>
              <IntlMsg
                {...messages.shortRun}
                values={{
                  dayPrefix: run.day == null ? '' : <IntlMsg {...messages.dayPrefix} values={{ day: run.day }} />,
                  stages: stageList(run),
                  make: pluralize(run.stages.length, 'makes', 'make'),
                  tracks: run.tracks,
                  groupCount: run.groupCount,
                  unseatedGroups: run.unseatedGroups,
                  groupsGet: pluralize(run.unseatedGroups, 'group gets', 'groups get'),
                  session
                }}
              />
            </Typography>
          </li>
        ))}
      </ul>
      {runs.length > 0 && (
        <>
          <Typography>
            <IntlMsg
              {...messages.fixesHeading}
              values={{ sessions, formatLink: <Link to="/format"><IntlMsg {...messages.formatLink} /></Link> }}
            />
          </Typography>
          <ul>
            {runs.map((run) => (
              <li key={run.stages[0]._id}>
                <Typography>
                  <IntlMsg {...messages.raiseSessionLimits} values={{ session, stages: stageList(run), groupCount: <Bold>{run.groupCount}</Bold> }} />
                </Typography>
              </li>
            ))}
            {limitChanges.length > 0 && (
              <li>
                <Typography>
                  <IntlMsg
                    {...messages.raisePlayerLimits}
                    values={{
                      athlete,
                      session,
                      groupCount: narrowestRun,
                      groups: pluralize(narrowestRun, 'group'),
                      changes: limitChanges.map((change, i) => (
                        <React.Fragment key={i}>{i > 0 && (i === limitChanges.length - 1 ? ' and ' : ', ')}{change}</React.Fragment>
                      ))
                    }}
                  />
                </Typography>
              </li>
            )}
          </ul>
        </>
      )}
    </Box>
  );
};
